import type { FC, ReactNode } from "react";
import { useState } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  copySharedProcessingMutationOptions,
  deleteSharedProcessingMutationOptions,
  sharedProcessingByIdQueryOptions,
  sharedProcessingHistoryQueryOptions,
} from "../api/sharedProcessings";
import type { RegistrationById } from "../api/registrations.types";
import { prefetch } from "../api/prefetch";
import { RegistrationControllerIcon2, FilesIcon, EditIcon } from "../shared/icons";
import { usePermission } from "../shared/permissions";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { trackEvent } from "../shared/Snowplow";
import type { DrawerHeaderProps } from "../shared/Drawer";
import { Drawer } from "../shared/Drawer";
import { ActionMenu, ActionMenuDeleteMenuItem, ActionMenuItemWithPermission } from "../shared/ActionMenu";
import { SharedProcessingEditDialog } from "./SharedProcessingEditDialog";

type SharedProcessingTab = "standard" | "guided" | "history";

type SharedProcessingDrawerHeaderProps = {
  sharedProcessing: RegistrationById;
  activeTab: SharedProcessingTab;
  statusIndicator: DrawerHeaderProps["status"];
  bottomSlot?: ReactNode;
};

export const SharedProcessingDrawerHeader: FC<SharedProcessingDrawerHeaderProps> = ({
  sharedProcessing,
  activeTab,
  statusIndicator,
  bottomSlot,
}) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const tabPath: Record<SharedProcessingTab, (id: string) => string> = {
    standard: paths.sharedProcessingsInline,
    guided: paths.sharedProcessingsMultiStep,
    history: paths.sharedProcessingsHistory,
  };
  const closeDrawer = () => navigate(paths.sharedProcessingsGrid());

  return (
    <Drawer.Header
      status={statusIndicator}
      onClose={closeDrawer}
      title={sharedProcessing.name}
      renderIcon={props => <RegistrationControllerIcon2 {...props} />}
      topRightSlot={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SharedProcessingActionMenu
            sharedProcessingId={sharedProcessing.id}
            onDeleted={closeDrawer}
            onCopied={id => navigate(tabPath[activeTab](id))}
            ariaLabelKey="GridDrawer.Menu.ButtonLabel"
          />
        </Box>
      }
      bottomSlot={
        <>
          <Stack
            direction="row"
            spacing={1}
            sx={{ px: Drawer.PADDING_X, alignItems: "flex-end", justifyContent: "space-between" }}
          >
            <Drawer.TabButtons>
              <Drawer.TabButton active={activeTab === "standard"} to={tabPath.standard(sharedProcessing.id)}>
                {t("Dynamic.Drawer.OverviewButton")}
              </Drawer.TabButton>
              <Drawer.TabButton
                active={activeTab === "guided"}
                to={tabPath.guided(sharedProcessing.id)}
                onClick={() => trackEvent.clickRegistrationGuidedTab()}
              >
                {t("RegistrationGrid.Drawer.GuidedViewButton")}
              </Drawer.TabButton>
              <Drawer.TabButton
                active={activeTab === "history"}
                to={tabPath.history(sharedProcessing.id)}
                onClick={() => trackEvent.clickRegistrationHistoryTab()}
                onHover={() => prefetch(sharedProcessingHistoryQueryOptions(sharedProcessing.id))}
              >
                {t("Dynamic.Drawer.HistoryButton")}
              </Drawer.TabButton>
            </Drawer.TabButtons>
          </Stack>
          {bottomSlot}
        </>
      }
    />
  );
};

export const SharedProcessingActionMenu = ({
  sharedProcessingId,
  onDeleted,
  onCopied,
  ariaLabelKey,
}: {
  sharedProcessingId: string;
  onDeleted?: () => void;
  onCopied: (id: string) => void;
  ariaLabelKey?: TranslationKey;
}) => {
  const { hasPermission } = usePermission();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const sharedProcessingQuery = useQuery(sharedProcessingByIdQueryOptions(sharedProcessingId, { enabled: isMenuOpen }));
  const { t } = useTranslation();
  const copyMutation = useMutation(copySharedProcessingMutationOptions());
  const deleteMutation = useMutation(deleteSharedProcessingMutationOptions());
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const missingManagePermission = !hasPermission("nova-forms-api:enterprise-customers:registrations:manage");

  return (
    <>
      {isEditDialogOpen && sharedProcessingQuery.data && (
        <SharedProcessingEditDialog
          onClose={() => {
            setIsEditDialogOpen(false);
            setIsMenuOpen(false);
          }}
          sharedProcessing={sharedProcessingQuery.data}
        />
      )}
      <ActionMenu
        isOpen={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        onTriggerHover={() => prefetch(sharedProcessingByIdQueryOptions(sharedProcessingId))}
        ariaLabelKey={ariaLabelKey}
      >
        <ActionMenuItemWithPermission
          missingPermission={missingManagePermission}
          disabled={sharedProcessingQuery.isLoading}
          onClick={() => setIsEditDialogOpen(true)}
        >
          <EditIcon />
          {t("RegistrationDrawer.Edit.Label")}
        </ActionMenuItemWithPermission>

        <ActionMenuItemWithPermission
          missingPermission={missingManagePermission}
          disabled={copyMutation.isPending || sharedProcessingQuery.isLoading}
          onClick={() => {
            if (!sharedProcessingQuery.data) throw new Error("Shared processing should always be defined here");
            trackEvent.copyRegistrationClick();
            copyMutation.mutate(
              {
                id: sharedProcessingId,
                name: `${sharedProcessingQuery.data.name} ${t("RegistrationDrawer.Registration.Copy")}`,
              },
              {
                onSuccess: copy => {
                  onCopied(copy.id);
                  setIsMenuOpen(false);
                },
              }
            );
          }}
        >
          <FilesIcon />
          {t("RegistrationDrawer.Copy.Label")}
        </ActionMenuItemWithPermission>

        <ActionMenuDeleteMenuItem
          missingPermission={missingManagePermission}
          disabled={sharedProcessingQuery.isLoading}
          confirmDeleteDialog={{
            header: t("RegistrationDrawer.Delete.ConfirmHeader"),
            message: (
              <Translate
                translationKey="RegistrationDrawer.Delete.ConfirmMessage"
                values={{
                  name: (
                    <Box component="span" key={sharedProcessingQuery.data?.name} sx={{ fontWeight: "bold" }}>
                      {sharedProcessingQuery.data?.name}
                    </Box>
                  ),
                }}
              />
            ),
            delete: t("RegistrationDrawer.Delete.ConfirmDelete"),
          }}
          onClose={() => setIsMenuOpen(false)}
          onDelete={() => {
            if (!sharedProcessingQuery.data) throw new Error("Shared processing should always be defined here.");

            return deleteMutation.mutateAsync(sharedProcessingQuery.data.id, {
              onSuccess: () => {
                onDeleted?.();
                setIsMenuOpen(false);
              },
            });
          }}
        />
      </ActionMenu>
    </>
  );
};
